/**
 * Exempt destinations — the shapes returned by /callcenter/admin/exempt-destinations.
 *
 * One of our own inbound numbers that no spam filter may reject on. This is the mirror image
 * of the whitelist: the whitelist exempts a CALLER, this exempts a DESTINATION.
 *
 * The list used to be a literal inside the inbound call handler, so adding a DID meant a
 * deploy. Everything here exists to make that a one-minute change that still leaves a record
 * of who made it and why.
 */

/** A user as this screen sees one — id and email, never the full CRM user record. */
export interface IActor {
    id: number
    email: string
}

/**
 * The `action` JSON on a history row: an action name, the operator's comment, and whatever
 * context that particular change recorded (the comment it replaced, when it was previously
 * removed). Declared as an interface rather than a class because of that open tail.
 */
export interface IHistoryAction {
    action: 'created' | 'updated' | 'deleted' | 'restored' | string
    comment?: string
    previous_comment?: string
    previously_removed_at?: string | null
    [key: string]: any
}

export class DestinationHistory {
    id: number = 0
    destination_id: number = 0
    user_id?: number | null = null
    action?: IHistoryAction
    created_at: string = ''
    user?: IActor | null
}

export class ExemptDestination {
    id?: number
    /** The 10 significant NANP digits — the only value the call handler ever matches. */
    phone_key: string = ''
    /** Display form, +1XXXXXXXXXX. */
    phone_e164: string = ''
    comment: string = ''
    created_by?: number | null
    updated_by?: number | null
    deleted_by?: number | null
    created_at?: string
    updated_at?: string
    /** Non-null means the exemption has been removed; the row and its history stay. */
    deleted_at?: string | null
    creator?: IActor | null
    updater?: IActor | null
    deletedBy?: IActor | null
    history?: DestinationHistory[]
}

/**
 * The detail endpoint also reports whether the exemption is live in the cache right now.
 *
 * A row in MySQL is not what the inbound webhook reads — the cached set is — so without this
 * flag "the admin says exempt but calls are still being filtered" cannot be diagnosed from
 * the screen.
 */
export class DestinationDetail {
    destination: ExemptDestination = new ExemptDestination()
    enforcement: { live_in_cache: boolean } = { live_in_cache: false }
}

export interface IDestinationFilters {
    q?: string
    include_removed?: boolean
}

export const isRemoved = (destination: ExemptDestination): boolean =>
    destination.deleted_at !== null && destination.deleted_at !== undefined

/** Human label for a history action, matching the wording used on the whitelist screens. */
export const actionLabel = (action?: IHistoryAction): string => {
    if (!action) {
        return ''
    }
    switch (action.action) {
        case 'created': return 'Added'
        case 'updated': return 'Reason edited'
        case 'deleted': return 'Removed'
        case 'restored': return 'Restored'
        default: return action.action
    }
}
